import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { retentionFor } from '@content-engine/shared';
import {
  archiveEntity,
  fetchAccounts,
  fetchBanners,
  fetchClip,
  fetchClipVariants,
  fetchVariant,
  generateClipForMoment,
  regenerateClipApi,
  shareTelegramApi,
  unarchiveEntity,
  updateClipApi,
} from '../../lib/queries.ts';
import type { Clip, ClipVariant } from '../../lib/types.ts';
import { ArchiveDialog, ArchivedChip } from '../../components/archive/index.ts';
import { BannerModal } from './BannerModal.tsx';
import { VariantLightbox } from './VariantLightbox.tsx';

export function ClipPreview({
  momentId,
  filmId,
  clip,
}: {
  /** Null when used outside a moment context (e.g. the /clips list page),
   *  where the clip always already exists and the "generate" UI is not shown. */
  momentId: string | null;
  filmId: string;
  clip: Clip | undefined;
}): React.ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [shareVariantId, setShareVariantId] = useState<string>('');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [showRender, setShowRender] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);

  const tgAccountsQuery = useQuery({
    queryKey: ['accounts', 'telegram'],
    queryFn: () => fetchAccounts({ platform: 'telegram', status: 'active'  }),
  });
  const tgAccounts = tgAccountsQuery.data?.accounts ?? [];

  // Banner-name lookup for the share dropdown options. react-query dedupes
  // across all ClipPreview instances on /clips, so this is one network call
  // per page, not per card. Status 'all' so even disabled banners still show
  // their name (we just want the human label).
  const bannersQuery = useQuery({
    queryKey: ['banners', 'all'],
    queryFn: () => fetchBanners({ status: 'all' }),
  });
  const bannerNameById = new Map(
    (bannersQuery.data?.banners ?? []).map((b) => [b.id, b.name]),
  );

  // ClipPreview is rendered from two places: the film-detail page (where the
  // active list is keyed by ['filmClips', filmId]) AND the /clips list page
  // (keyed by ['clips']). Invalidate BOTH so either parent refreshes.
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['filmClips', filmId] });
    void queryClient.invalidateQueries({ queryKey: ['clips'] });
  };

  useEffect(() => {
    if (!selectedAccountId && tgAccounts.length > 0) {
      const first = tgAccounts[0];
      if (first) setSelectedAccountId(first.id);
    }
  }, [tgAccounts, selectedAccountId]);

  const generateMutation = useMutation({
    mutationFn: () =>
      momentId
        ? generateClipForMoment(momentId)
        : Promise.reject(new Error('no momentId')),
    onSuccess: invalidate,
  });

  const regenerateMutation = useMutation({
    mutationFn: () =>
      clip
        ? regenerateClipApi(clip.id)
        : Promise.reject(new Error('no clip')),
    onSuccess: invalidate,
  });

  // Удаления в пользовательском UI нет: клип уходит в архив вместе со своим
  // моментом (это одна сущность), а его варианты остаются активными.
  const archiveMutation = useMutation({
    mutationFn: (opts: { freeFiles: boolean }) =>
      clip ? archiveEntity('clip', clip.id, opts) : Promise.reject(new Error('no clip')),
    onSuccess: () => {
      setShowArchiveDialog(false);
      void queryClient.invalidateQueries({ queryKey: ['moments', filmId] });
      invalidate();
    },
    // Без этого 404 «already archived» просто оставлял диалог висеть.
    onError: (error: Error) => {
      setShowArchiveDialog(false);
      alert(error.message);
    },
  });

  const unarchiveMutation = useMutation({
    mutationFn: () =>
      clip ? unarchiveEntity('clip', clip.id) : Promise.reject(new Error('no clip')),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['moments', filmId] });
      invalidate();
    },
    onError: (error: Error) => alert(error.message),
  });

  const variantsQuery = useQuery({
    queryKey: ['clipVariants', clip?.id],
    queryFn: () => (clip ? fetchClipVariants(clip.id) : Promise.resolve({ variants: [] as ClipVariant[] })),
    enabled: !!clip && (clip.status === 'ready' || clip.status === 'approved' || clip.status === 'rejected'),
    refetchInterval: (q) => {
      const list = q.state.data?.variants ?? [];
      return list.some((v) => v.status === 'pending' || v.status === 'rendering') ? 3_000 : false;
    },
  });
  const variants = variantsQuery.data?.variants ?? [];

  // Если выбранный к отправке вариант успели заархивировать — снимаем выбор,
  // иначе select показывал бы «Оригинал», а отправился бы архивный вариант.
  useEffect(() => {
    if (shareVariantId && !variants.some((v) => v.id === shareVariantId && !v.archivedAt)) {
      setShareVariantId('');
    }
  }, [variants, shareVariantId]);

  const shareTelegramMutation = useMutation({
    mutationFn: () =>
      clip && selectedAccountId
        ? shareTelegramApi(clip.id, {
            accountId: selectedAccountId,
            ...(shareVariantId ? { variantId: shareVariantId } : {}),
          })
        : Promise.reject(new Error('no clip or account')),
    onSuccess: (result) => {
      alert(t('clips.shareTelegramSuccess', { messageId: result.messageId ?? '?' }));
    },
    onError: (error: Error) => {
      alert(t('clips.shareTelegramFail', { message: error.message }));
    },
  });

  const detailQuery = useQuery({
    queryKey: ['clip', clip?.id],
    queryFn: () => fetchClip(clip!.id),
    enabled: !!clip && (clip.status === 'ready' || clip.status === 'approved' || clip.status === 'rejected'),
  });

  /** Для АРХИВНОГО клипа «В архив» — это 404 «already archived». Показываем
   *  обратное действие: вернуть из архива можно прямо отсюда. */
  const archiveToggle = (style?: CSSProperties): React.ReactElement =>
    clip?.archivedAt ? (
      <button
        type="button"
        onClick={() => unarchiveMutation.mutate()}
        disabled={unarchiveMutation.isPending}
        style={style}
      >
        {t('clips.detail.unarchive')}
      </button>
    ) : (
      <button
        type="button"
        onClick={() => setShowArchiveDialog(true)}
        title={t('clips.detail.archive')}
        style={style}
      >
        {t('clips.detail.archive')}
      </button>
    );

  // Срок и объём — из бэкенда (retention роли + размер foundation-файла), не из UI.
  const archiveDialog =
    clip && showArchiveDialog ? (
      <ArchiveDialog
        kind="clip"
        freeAfterDays={retentionFor('clip_foundation')}
        sizeBytes={detailQuery.data?.file.sizeBytes ?? clip.sizeBytes}
        variantsCount={variants.filter((v) => !v.archivedAt).length}
        busy={archiveMutation.isPending}
        onCancel={() => setShowArchiveDialog(false)}
        onConfirm={(opts) => archiveMutation.mutate(opts)}
      />
    ) : null;

  if (!clip) {
    return (
      <div style={{ padding: 8, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={() => generateMutation.mutate()} disabled={generateMutation.isPending}>
          {generateMutation.isPending ? '…' : t('clips.generate')}
        </button>
      </div>
    );
  }

  if (clip.status === 'pending' || clip.status === 'rendering') {
    return (
      <div className="working" style={{ padding: 8 }}>
        <span className="spinner" /> {t(`clips.status_${clip.status}`)}
      </div>
    );
  }

  if (clip.status === 'error') {
    return (
      <div style={{ padding: 8 }}>
        <div style={{ color: 'var(--hot)', fontSize: 11, marginBottom: 4 }}>
          {clip.renderError ?? 'Render failed'}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => regenerateMutation.mutate()}>{t('clips.regenerate')}</button>
          {archiveToggle()}
        </div>
        {archiveDialog}
      </div>
    );
  }

  const url = detailQuery.data?.presignedUrl;

  return (
    <div style={{ padding: 8 }}>
      {/* Foundation card — a clearly-bounded unit so its actions read as
          clip-level (the base clip, no banner), distinct from the variants
          section below where each variant has its own actions. */}
      <div style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 10, background: 'var(--bg-card)' }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{t('clips.foundationLabel')}</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          {url && (
            <video
              src={url}
              controls
              style={{ width: 240, height: 427, background: '#000', borderRadius: 4 }}
            />
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className={`pill ${clip.status === 'approved' ? 'live' : ''}`}>
            {t(`clips.status_${clip.status}`)}
          </span>
          {url && (
            <a href={url} download={`clip-${clip.id}.mp4`} style={{ fontSize: 12, color: 'var(--neon)' }}>
              {t('clips.download')}
            </a>
          )}
          {/* Primary moderation decision — prominent */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            <button className="primary" onClick={() => updateClipApi(clip.id, { status: 'approved' }).then(invalidate)}>
              ✓ {t('clips.approve')}
            </button>
            <button onClick={() => {
              const reason = prompt(t('clips.rejectReasonPrompt')) ?? '';
              void updateClipApi(clip.id, { status: 'rejected', rejectedReason: reason }).then(invalidate);
            }}>
              ✕ {t('clips.reject')}
            </button>
          </div>
          {/* Secondary content actions — quieter; delete pushed away to avoid mis-click */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
            <button onClick={() => setShowBannerModal(true)}>+ {t('clips.addVariant')}</button>
            <button onClick={() => setShowRender((v) => !v)} aria-expanded={showRender}>
              ↻ {t('clips.renderSettings')} {showRender ? '▴' : '▾'}
            </button>
            {archiveToggle({ marginLeft: 'auto', fontSize: 11, padding: '2px 8px' })}
          </div>
          {/* Collapsible re-render panel */}
          {showRender && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 6, padding: 8, background: 'var(--bg-elev)', borderRadius: 6 }}>
              <button onClick={() => regenerateMutation.mutate()}>{t('clips.regenerate')}</button>
            </div>
          )}
            <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 8 }}>
              {t('clips.foundationActionsHint')}
            </div>
          </div>
        </div>
      </div>

      {/* Variants section */}
      {variants.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8 }}>
            {t('clips.variantsLabel')} ({variants.length})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
            {variants.map((v, i) => (
              <VariantCard
                key={v.id}
                variant={v}
                onOpen={() => setLightboxIndex(i)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Share section */}
      <div style={{ marginTop: 16, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('clips.shareSectionLabel')}:</strong>
        {tgAccounts.length > 0 ? (
          <>
            <select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              style={{ fontSize: 11, padding: '2px 6px' }}
              title={t('clips.shareAccountLabel')}
            >
              {tgAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.displayName ?? a.handle} ({a.handle})
                </option>
              ))}
            </select>
            <select
              value={shareVariantId}
              onChange={(e) => setShareVariantId(e.target.value)}
              style={{ fontSize: 11, padding: '2px 6px' }}
              title={t('clips.shareWhatLabel')}
            >
              <option value="">{t('clips.shareWhatFoundation')}</option>
              {variants
                // Архивный вариант нельзя предлагать к публикации: его убрали
                // из работы осознанно, публикация вслепую — потеря контроля.
                .filter((v) => !v.archivedAt)
                .filter((v) => v.status === 'ready' || v.status === 'approved' || v.status === 'rejected')
                .map((v, i) => (
                  <option key={v.id} value={v.id}>
                    {t('clips.shareWhatVariant', {
                      index: i + 1,
                      banner: (v.bannerLibraryId ? bannerNameById.get(v.bannerLibraryId) : null) ?? '—',
                      status: t(`clips.variantStatus_${v.status}`),
                    })}
                  </option>
                ))}
            </select>
            <button
              onClick={() => shareTelegramMutation.mutate()}
              disabled={shareTelegramMutation.isPending || !selectedAccountId}
            >
              {t('clips.shareTelegram')}
            </button>
          </>
        ) : (
          <button disabled title={t('clips.shareNoAccountsHint')}>
            {t('clips.shareTelegram')}
          </button>
        )}
      </div>

      {showBannerModal && (
        <BannerModal
          clipId={clip.id}
          onClose={() => setShowBannerModal(false)}
          onApplied={() => {
            setShowBannerModal(false);
            void queryClient.invalidateQueries({ queryKey: ['clipVariants', clip.id] });
          }}
        />
      )}

      {lightboxIndex !== null && variants[lightboxIndex] && (
        <VariantLightbox
          variants={variants}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
          onChange={() => {
            void queryClient.invalidateQueries({ queryKey: ['clipVariants', clip.id] });
          }}
        />
      )}

      {archiveDialog}
    </div>
  );
}

function VariantCard({
  variant,
  onOpen,
}: {
  variant: ClipVariant;
  onOpen: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const detailQuery = useQuery({
    queryKey: ['clipVariant', variant.id],
    queryFn: () => fetchVariant(variant.id),
    enabled: variant.status === 'ready' || variant.status === 'approved' || variant.status === 'rejected',
  });
  const url = detailQuery.data?.presignedUrl;
  // The whole card opens the lightbox (even for pending/error variants) where
  // all actions — approve/reject/regenerate/archive — live at a usable size.
  // Архивный вариант сюда попадать не должен (лента чистится выше), но если
  // попал — он обязан выглядеть архивным, а не активным.
  return (
    <div
      onClick={onOpen}
      className={variant.archivedAt ? 'arch' : undefined}
      title={t('clips.variantLightboxOpen')}
      style={{ border: '1px solid var(--line)', borderRadius: 6, overflow: 'hidden', background: 'var(--bg-card)', cursor: 'pointer' }}
    >
      <div style={{ aspectRatio: variant.width && variant.height ? `${variant.width} / ${variant.height}` : '9 / 16', background: '#000', position: 'relative' }}>
        {url ? (
          <>
            <video src={url} muted style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
            <div
              style={{
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0)', opacity: 0, transition: 'opacity 0.15s, background 0.15s', color: '#fff', fontSize: 28,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'rgba(0,0,0,0.35)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '0'; e.currentTarget.style.background = 'rgba(0,0,0,0)'; }}
            >
              ⛶
            </div>
          </>
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 11 }}>
            {variant.status === 'pending' || variant.status === 'rendering'
              ? <span className="working"><span className="spinner" /> {t(`clips.variantStatus_${variant.status}`)}</span>
              : t(`clips.variantStatus_${variant.status}`)}
          </div>
        )}
      </div>
      <div style={{ padding: 8 }}>
        <div style={{ fontSize: 10, color: 'var(--text-3)', marginBottom: 4 }}>
          {variant.position} · {variant.sizePct}%
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
          <span className="pill" style={{ fontSize: 10 }}>
            {t(`variants.bgMode.${variant.backgroundMode}`)}
          </span>
          {variant.burnSubtitles ? (
            <span className="pill live" style={{ fontSize: 10 }}>
              {t('variants.subs.shownWithPosition', { position: t(`variants.subs.${variant.subtitlePosition}`) })}
            </span>
          ) : (
            <span className="pill idle" style={{ fontSize: 10 }}>{t('variants.subs.hidden')}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span className={`pill ${variant.status === 'approved' ? 'live' : ''}`} style={{ fontSize: 10 }}>
            {t(`clips.variantStatus_${variant.status}`)}
          </span>
          <ArchivedChip archivedAt={variant.archivedAt} />
          {url && (
            <a
              href={url}
              download={`clip-${variant.clipId}-variant-${variant.id}.mp4`}
              onClick={(e) => e.stopPropagation()}
              title={t('clips.download')}
              style={{ fontSize: 11, color: 'var(--neon)', textDecoration: 'none', marginLeft: 'auto' }}
            >
              ↓
            </a>
          )}
        </div>
        {variant.renderError && (
          <div style={{ fontSize: 9, color: 'var(--hot)', marginTop: 2 }}>{variant.renderError.slice(0, 80)}</div>
        )}
      </div>
    </div>
  );
}
